import { Link } from "react-router-dom";
import { fmtMoney } from "@/lib/api";
import {
  evaluateUploadResult,
  intentByValue,
  sortedMesKeys,
  EXCEL_UPLOAD_INTENTS,
  formatExcelLastUpload,
} from "@/lib/excelContabilidadPreflight";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

function MesTable({ title, resumen, testId }) {
  const porMes = resumen?.por_mes || {};
  const keys = sortedMesKeys(porMes);
  if (!keys.length && !(resumen?.filas > 0)) return null;
  return (
    <div className="mt-3" data-testid={testId}>
      <p className="text-[11px] font-semibold text-slate-800">{title}</p>
      <p className="text-[10px] text-slate-600 mb-1">
        {resumen?.filas ?? 0} fila(s) · total {fmtMoney(resumen?.totales?.total ?? 0)}
      </p>
      {keys.length > 0 && (
        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="w-full text-[10px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-2 py-1">Mes</th>
                <th className="text-right px-2 py-1">Filas</th>
                <th className="text-right px-2 py-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((m) => (
                <tr key={m} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-mono">{m}</td>
                  <td className="px-2 py-1 text-right">{porMes[m]?.count ?? 0}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(porMes[m]?.total ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Resumen post-carga y avisos de flujo (ingreso vs egreso). */
export default function ExcelCargaResumenPanel({ uploadIntent, lastResult }) {
  if (!lastResult || lastResult.kind !== "excel") return null;

  const evalResult = evaluateUploadResult(uploadIntent, lastResult);
  const intent = intentByValue(uploadIntent);
  const Icon = evalResult.severity === "ok" ? CheckCircle2 : evalResult.severity === "block" ? XCircle : AlertTriangle;
  const boxClass =
    evalResult.severity === "ok"
      ? "border-emerald-200 bg-emerald-50/90 text-emerald-950"
      : evalResult.severity === "block"
        ? "border-red-200 bg-red-50/90 text-red-950"
        : "border-amber-200 bg-amber-50/90 text-amber-950";
  const yearBd = lastResult.carga_resumen_bd?.year;
  const resIngShow = evalResult.resumenIngresoDisplay ?? lastResult.carga_resumen?.ingreso;
  const resEgrShow = evalResult.resumenEgresoDisplay ?? lastResult.carga_resumen?.egreso;
  const resEgrArchivo = lastResult.carga_resumen?.egreso;
  const resIngArchivo = lastResult.carga_resumen?.ingreso;

  return (
    <div
      className={`rounded-sm border px-4 py-3 text-sm mt-4 ${boxClass}`}
      data-testid="excel-carga-resumen-panel"
    >
      <div className="flex gap-2 items-start">
        <Icon className="w-5 h-5 shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {evalResult.intentAlreadyInBd
              ? `Información disponible (${intent.label})`
              : `Resultado de la carga (${intent.label})`}
          </p>
          <p className="text-[11px] mt-1 opacity-90">
            Archivo detectado: <strong>{evalResult.fileDir || "—"}</strong>
            {" · "}
            {lastResult.total} fila(s) persistidas en esta importación
            {lastResult.replaced_months?.length
              ? ` · meses: ${lastResult.replaced_months.slice(0, 6).join(", ")}${lastResult.replaced_months.length > 6 ? "…" : ""}`
              : ""}
          </p>
          {evalResult.issues.map((issue) => (
            <p key={issue.code} className="text-[11px] mt-2 font-medium">
              {issue.message}
            </p>
          ))}
          {evalResult.intentAlreadyInBd && uploadIntent === "ingreso" && (
            <p className="text-[11px] mt-2">
              <Link to="/confronta-cfdi" className="underline font-semibold">
                Ir a Confronta Ingresos
              </Link>
              {" "}para cruzar con el SAT.
            </p>
          )}
          {evalResult.intentAlreadyInBd && uploadIntent === "egreso" && (
            <p className="text-[11px] mt-2">
              <Link to="/confronta-cfdi" className="underline font-semibold">
                Ir a Confronta Egresos
              </Link>
              {" "}para cruzar con el SAT.
            </p>
          )}
          {evalResult.severity === "block" && uploadIntent === "ingreso" && (
            <p className="text-[11px] mt-2">
              <Link to="/confronta-cfdi" className="underline font-semibold">
                Ir a Confronta Egresos
              </Link>
              {" "}si este archivo era solo de gastos.
            </p>
          )}
          {evalResult.severity === "block" && uploadIntent === "egreso" && (
            <p className="text-[11px] mt-2">
              <Link to="/confronta-cfdi" className="underline font-semibold">
                Ir a Confronta Ingresos
              </Link>
              {" "}si el archivo era de ventas.
            </p>
          )}
          <MesTable
            title={
              evalResult.intentAlreadyInBd && uploadIntent === "ingreso"
                ? `Ingresos en el sistema${yearBd ? ` (${yearBd})` : ""}`
                : "Ingresos guardados"
            }
            resumen={resIngShow}
            testId="excel-carga-resumen-ingreso"
          />
          {evalResult.intentAlreadyInBd && uploadIntent === "ingreso" && (resEgrArchivo?.filas > 0) && (
            <MesTable
              title="Egresos de este archivo"
              resumen={resEgrArchivo}
              testId="excel-carga-resumen-egreso-archivo"
            />
          )}
          {!(evalResult.intentAlreadyInBd && uploadIntent === "ingreso") && (
            <MesTable
              title="Egresos guardados"
              resumen={resEgrShow}
              testId="excel-carga-resumen-egreso"
            />
          )}
          {evalResult.intentAlreadyInBd && uploadIntent === "egreso" && (resIngArchivo?.filas > 0) && (
            <MesTable
              title="Ingresos de este archivo"
              resumen={resIngArchivo}
              testId="excel-carga-resumen-ingreso-archivo"
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Selector de intención antes de subir Excel. */
export function ExcelUploadIntentPicker({ value, onChange, disabled, lastImport, year }) {
  return (
    <div className="space-y-2" data-testid="excel-upload-intent-picker">
      <p className="text-[11px] font-semibold text-slate-800">¿Qué información trae este archivo?</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {EXCEL_UPLOAD_INTENTS.map((opt) => {
          const slot = lastImport?.[opt.value];
          const hasData = Number(slot?.filas ?? 0) > 0;
          const lastNote = formatExcelLastUpload(slot?.uploaded_at, {
            filas: slot?.filas,
            year,
          });
          return (
            <label
              key={opt.value}
              className={`flex items-start gap-2 rounded-sm border px-3 py-2 cursor-pointer text-[11px] flex-1 min-w-[200px] ${
                value === opt.value
                  ? "border-[#0F2D5C] bg-[#0F2D5C]/5 ring-1 ring-[#0F2D5C]/20"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            >
              <input
                type="radio"
                name="excel-upload-intent"
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
                className="mt-0.5"
                disabled={disabled}
              />
              <span>
                <span className="font-semibold text-slate-900 block">{opt.label}</span>
                <span className="text-slate-600">{opt.hint}</span>
                <span
                  className={`block mt-1.5 text-[10px] leading-snug ${
                    hasData ? "text-emerald-800 font-medium" : "text-slate-500"
                  }`}
                  data-testid={`excel-last-import-${opt.value}`}
                >
                  {lastNote}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
